import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Plus, Pencil, Trash2, X, Save, Upload, Sparkles } from "lucide-react";

const CATEGORIES = ["services", "bible-study", "youth", "ministry", "special", "general"];

const EMPTY = {
  title: "",
  description: "",
  event_date: "",
  start_time: "",
  end_time: "",
  location: "",
  category: "services",
};

export default function EventManager() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | event.id
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("upcoming"); // upcoming | past | all
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/events");
      setEvents(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const visible = events.filter((e) => {
    if (filter === "upcoming") return e.event_date >= today;
    if (filter === "past") return e.event_date < today;
    return true;
  });

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (e) => {
    setForm({
      title: e.title || "",
      description: e.description || "",
      event_date: e.event_date || "",
      start_time: e.start_time || "",
      end_time: e.end_time || "",
      location: e.location || "",
      category: e.category || "services",
    });
    setEditing(e.id);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (ev) => {
    ev.preventDefault();
    if (!form.title || !form.event_date) {
      toast.error("Title and date are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // strip empty optionals so backend stores cleanly
      ["description", "start_time", "end_time", "location"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (editing === "new") {
        await api.post("/events", payload);
        toast.success("Event created");
      } else {
        await api.put(`/events/${editing}`, payload);
        toast.success("Event updated");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="event-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-serif text-2xl font-bold">Church Calendar</h3>
          <p className="text-sm text-white/55">Add, edit, or remove events shown on the public calendar & homepage.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setShowImport(!showImport); setEditing(null); }}
            className="btn-ghost px-5 py-3 rounded-full text-sm uppercase tracking-widest font-semibold inline-flex items-center gap-2 border border-white/15"
            data-testid="admin-event-import-toggle"
          >
            <Upload className="h-4 w-4" /> {showImport ? "Close Import" : "Bulk Import"}
          </button>
          <button
            onClick={() => { openNew(); setShowImport(false); }}
            className="btn-wine px-6 py-3 rounded-full text-sm uppercase tracking-widest font-semibold inline-flex items-center gap-2"
            data-testid="admin-event-new"
          >
            <Plus className="h-4 w-4" /> New Event
          </button>
        </div>
      </div>

      {/* Bulk Import panel */}
      {showImport && (
        <BulkImportPanel
          onDone={(inserted) => {
            setShowImport(false);
            toast.success(`${inserted} event${inserted === 1 ? "" : "s"} imported`);
            load();
          }}
        />
      )}

      {/* Filter pills */}
      <div className="flex gap-2 mb-6" data-testid="event-filter">
        {[
          { k: "upcoming", label: `Upcoming (${events.filter((e) => e.event_date >= today).length})` },
          { k: "past",     label: `Past (${events.filter((e) => e.event_date < today).length})` },
          { k: "all",      label: `All (${events.length})` },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full font-semibold transition-colors ${
              filter === k ? "bg-gold/15 text-gold border border-gold/40" : "text-white/55 border border-white/15 hover:text-white"
            }`}
            data-testid={`event-filter-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Inline editor */}
      {editing && (
        <form
          onSubmit={save}
          className="glass p-8 mb-8 grid md:grid-cols-2 gap-x-8 gap-y-5"
          data-testid="event-editor"
        >
          <div className="md:col-span-2 flex items-center justify-between pb-3 border-b border-white/10">
            <h4 className="font-serif text-xl font-bold">
              {editing === "new" ? "New Event" : "Edit Event"}
            </h4>
            <button type="button" onClick={cancel} className="text-white/60 hover:text-white p-1" data-testid="event-cancel">
              <X className="h-5 w-5" />
            </button>
          </div>

          <Field label="Title" required>
            <input data-testid="event-title" required value={form.title} onChange={u("title")} className={inputCls} />
          </Field>
          <Field label="Category">
            <select data-testid="event-category" value={form.category} onChange={u("category")} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-wine-950">{c}</option>)}
            </select>
          </Field>

          <Field label="Date" required>
            <input data-testid="event-date" type="date" required value={form.event_date} onChange={u("event_date")} className={inputCls} />
          </Field>
          <Field label="Location">
            <input data-testid="event-location" value={form.location} onChange={u("location")} className={inputCls} placeholder="Main Sanctuary" />
          </Field>

          <Field label="Start Time">
            <input data-testid="event-start" type="time" value={form.start_time} onChange={u("start_time")} className={inputCls} />
          </Field>
          <Field label="End Time">
            <input data-testid="event-end" type="time" value={form.end_time} onChange={u("end_time")} className={inputCls} />
          </Field>

          <Field label="Description" className="md:col-span-2">
            <textarea
              data-testid="event-description"
              rows={3}
              value={form.description}
              onChange={u("description")}
              className={inputCls}
            />
          </Field>

          <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-white/10">
            <button type="button" onClick={cancel} className="btn-ghost px-6 py-3 rounded-full text-xs uppercase tracking-widest font-semibold">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-wine px-7 py-3 rounded-full text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="event-save"
            >
              <Save className="h-4 w-4" /> {saving ? "Saving…" : (editing === "new" ? "Create Event" : "Save Changes")}
            </button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="space-y-px bg-white/5" data-testid="event-list">
        {visible.length === 0 && (
          <div className="bg-wine-950 p-10 text-center text-white/50 italic">
            No {filter === "all" ? "" : filter} events.
          </div>
        )}
        {visible.map((e) => (
          <div key={e.id} className="bg-wine-950 p-6 grid md:grid-cols-12 gap-6 items-center" data-testid={`event-row-${e.id}`}>
            <div className="md:col-span-2">
              <DateBadge dateStr={e.event_date} />
            </div>
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-widest text-gold font-bold mb-1">{e.category || "general"}</div>
              <h4 className="font-serif text-xl font-bold">{e.title}</h4>
              <div className="text-xs text-white/55 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {e.start_time && <span>{e.start_time}{e.end_time ? ` – ${e.end_time}` : ""}</span>}
                {e.location && <span>{e.location}</span>}
              </div>
              {e.description && (
                <p className="text-sm text-white/65 mt-2 line-clamp-2">{e.description}</p>
              )}
            </div>
            <div className="md:col-span-3 flex justify-end gap-2">
              <button
                onClick={() => openEdit(e)}
                className="border border-white/15 text-white/80 hover:text-gold hover:border-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-2"
                data-testid={`event-edit-${e.id}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                onClick={() => del(e.id, e.title)}
                className="border border-white/15 text-white/70 hover:text-red-400 hover:border-red-400 p-2 rounded-full"
                data-testid={`event-delete-${e.id}`}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-0 border-b border-white/15 text-white px-0 py-2.5 focus:outline-none focus:border-gold transition-colors";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">
        {label}{required && <span className="text-gold ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}

function DateBadge({ dateStr }) {
  // Parse "YYYY-MM-DD" without timezone shift
  const parts = (dateStr || "").split("-");
  if (parts.length !== 3) return <div className="text-white/40 text-sm">—</div>;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const month = d.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const day = d.getDate();
  const weekday = d.toLocaleString("en-US", { weekday: "short" }).toUpperCase();
  return (
    <div className="inline-flex flex-col items-center justify-center w-20 h-20 border border-gold/40 bg-wine-900/60">
      <div className="text-[10px] tracking-[0.25em] text-gold font-bold">{month}</div>
      <div className="text-3xl font-black text-white leading-none">{day}</div>
      <div className="text-[10px] tracking-widest text-white/55 mt-0.5">{weekday}</div>
    </div>
  );
}

/* ============ BULK IMPORT ============ */

const IMPORT_TEMPLATE = `title,event_date,start_time,end_time,location,category,description
Sunday Worship,2026-10-05,10:00,12:00,Main Sanctuary,services,Sunday morning worship service.
Bible Study,2026-10-08,19:00,20:30,Chapel,bible-study,Mid-week Bible Study.`;

function BulkImportPanel({ onDone }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState([]); // preview rows
  const [errors, setErrors] = useState([]);
  const [inserting, setInserting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const parseCSV = (raw) => {
    // Simple CSV parser supporting quoted fields
    const rows = [];
    let row = [], cur = "", inQuotes = false;
    for (let i = 0; i < raw.length; i++) {
      const c = raw[i];
      if (inQuotes) {
        if (c === '"' && raw[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQuotes = false; }
        else { cur += c; }
      } else {
        if (c === '"') { inQuotes = true; }
        else if (c === ",") { row.push(cur); cur = ""; }
        else if (c === "\n" || c === "\r") {
          if (c === "\r" && raw[i + 1] === "\n") i++;
          row.push(cur); rows.push(row); row = []; cur = "";
        } else { cur += c; }
      }
    }
    if (cur || row.length) { row.push(cur); rows.push(row); }
    return rows.filter((r) => r.some((v) => v && v.trim()));
  };

  const normalizeDate = (s) => {
    if (!s) return "";
    s = s.trim();
    // Already ISO?
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // Handle M/D/YYYY or MM/DD/YY
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (m) {
      let [, mo, dy, yr] = m;
      yr = yr.length === 2 ? "20" + yr : yr;
      return `${yr}-${String(mo).padStart(2, "0")}-${String(dy).padStart(2, "0")}`;
    }
    return s; // let backend reject
  };

  const normalizeTime = (s) => {
    if (!s) return "";
    s = s.trim();
    // Already HH:MM?
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      const [h, mi] = s.split(":");
      return `${h.padStart(2, "0")}:${mi}`;
    }
    // 10:00 AM style
    const m = s.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)?$/);
    if (m) {
      let h = parseInt(m[1], 10);
      let mi = m[2] ? parseInt(m[2], 10) : 0;
      const ap = (m[3] || "").toUpperCase();
      if (ap === "PM" && h < 12) h += 12;
      if (ap === "AM" && h === 12) h = 0;
      return `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
    }
    return s;
  };

  const doParse = () => {
    const rows = parseCSV(text);
    if (rows.length < 2) { setParsed([]); setErrors(["Need a header row + at least one data row."]); return; }
    const header = rows[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    const need = ["title", "event_date"];
    const missing = need.filter((n) => !header.includes(n));
    if (missing.length) {
      setParsed([]); setErrors([`Missing required columns: ${missing.join(", ")}`]);
      return;
    }
    const out = [];
    const errs = [];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      const rec = {};
      header.forEach((h, idx) => { rec[h] = (r[idx] || "").trim(); });
      if (!rec.title) continue;
      const ev = {
        title: rec.title,
        event_date: normalizeDate(rec.event_date),
        category: (rec.category || "general").toLowerCase(),
      };
      if (rec.description) ev.description = rec.description;
      if (rec.start_time) ev.start_time = normalizeTime(rec.start_time);
      if (rec.end_time)   ev.end_time = normalizeTime(rec.end_time);
      if (rec.location)   ev.location = rec.location;

      if (!/^\d{4}-\d{2}-\d{2}$/.test(ev.event_date)) {
        errs.push(`Row ${i + 1}: bad date "${rec.event_date}" — please use YYYY-MM-DD or M/D/YYYY`);
      } else {
        out.push(ev);
      }
    }
    setParsed(out); setErrors(errs);
  };

  const doImport = async () => {
    if (!parsed.length) return;
    setInserting(true);
    setProgress({ done: 0, total: parsed.length });
    let ok = 0;
    for (let i = 0; i < parsed.length; i++) {
      try {
        await api.post("/events", parsed[i]);
        ok++;
      } catch (err) {
        // continue on individual failure
        console.error("Import row failed", parsed[i], err);
      }
      setProgress({ done: i + 1, total: parsed.length });
    }
    setInserting(false);
    onDone(ok);
  };

  return (
    <div className="glass p-8 mb-8" data-testid="event-import-panel">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="h-5 w-5 text-gold" />
        <h4 className="font-serif text-xl font-bold">Bulk Import Events</h4>
      </div>
      <p className="text-sm text-white/60 mb-4">
        Paste CSV rows from Google Sheets, Excel, or your event tracker. First row must be the header.
        <span className="text-gold"> Required:</span> <code className="text-gold">title, event_date</code>.
        Optional: <code>start_time, end_time, location, category, description</code>.
      </p>

      <textarea
        rows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={IMPORT_TEMPLATE}
        className="w-full bg-wine-950/70 border border-white/15 text-white p-4 font-mono text-xs focus:outline-none focus:border-gold"
        data-testid="event-import-textarea"
      />

      <div className="flex flex-wrap gap-3 mt-4">
        <button
          onClick={doParse}
          className="btn-ghost border border-white/15 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-semibold"
          data-testid="event-import-preview"
        >
          Preview
        </button>
        <button
          onClick={() => setText(IMPORT_TEMPLATE)}
          className="btn-ghost text-xs uppercase tracking-widest text-white/60 hover:text-gold px-4 py-2.5"
          data-testid="event-import-template"
        >
          Insert template
        </button>
        {parsed.length > 0 && (
          <button
            onClick={doImport}
            disabled={inserting}
            className="btn-wine ml-auto px-7 py-2.5 rounded-full text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="event-import-submit"
          >
            <Upload className="h-4 w-4" />
            {inserting
              ? `Importing… ${progress.done}/${progress.total}`
              : `Create ${parsed.length} Event${parsed.length === 1 ? "" : "s"}`}
          </button>
        )}
      </div>

      {errors.length > 0 && (
        <div className="mt-5 border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200 space-y-1" data-testid="event-import-errors">
          {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
        </div>
      )}

      {parsed.length > 0 && (
        <div className="mt-6" data-testid="event-import-preview-list">
          <div className="text-xs uppercase tracking-widest text-gold font-bold mb-3">
            Preview — {parsed.length} row{parsed.length === 1 ? "" : "s"} ready
          </div>
          <div className="max-h-72 overflow-y-auto border border-white/10 divide-y divide-white/5">
            {parsed.slice(0, 100).map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 p-3 text-sm items-center">
                <div className="col-span-2 text-gold font-mono text-xs">{r.event_date}</div>
                <div className="col-span-2 text-white/60 text-xs">{r.start_time || "—"}</div>
                <div className="col-span-1 text-white/50 text-[10px] uppercase tracking-wider">{r.category}</div>
                <div className="col-span-7 truncate">{r.title}</div>
              </div>
            ))}
            {parsed.length > 100 && (
              <div className="p-3 text-xs text-white/50 italic">+{parsed.length - 100} more…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

